import axios, { type AxiosResponse } from 'axios';

import keyClockConfig, { LoginConstants } from '../../config/keyClock.config';
import { getAPIUrls } from '../../util/env';
import { parseJwt } from '../../util/jwt';
import { applyTheme } from '../../services/theme';
import { realmRoleInContext } from '../../services/entity-resolution';
import type { TokenResponse, JwtPayload } from '../../types/auth.types';

export const getCodeVerifierFromSessionStorage = (): string | null => {
  return sessionStorage.getItem(LoginConstants.CODE_VERIFIER);
};

/**
 * The three calls below (token exchange, refresh, logout) stay on raw axios
 * as a documented exception alongside bulk-import.
 *
 * Reason: `baseQueryWithAuth` (the shared RTK Query base query) calls
 * `getValidAccessToken()` to attach a Bearer header, which in turn calls
 * back into this file's `getAccessTokenFromRefreshTokenAPI` whenever the
 * stored token has expired. Routing these endpoints *through* that base
 * query would create a chicken-and-egg loop: the call that mints the token
 * would itself need a valid token to dispatch.
 *
 * The refresh and logout helpers also create a fresh axios instance so
 * the global request/response interceptors don't re-enter this code on a
 * 401 from the refresh endpoint itself.
 *
 * Post-login `getUserDetails` was previously here for symmetry but moved
 * to `orderApi.useLazyGetExternalUserQuery` in Pass 3 — consumers
 * (Loader, CallBack) call the hook directly.
 */
export const getAccessTokenFromApi = (
  code: string,
  codeVerifier: string,
): Promise<AxiosResponse<TokenResponse>> => {
  return axios.post(getAPIUrls('/api/v1/token'), {
    code,
    codeVerifier,
    redirectUri: keyClockConfig.redirectUri,
  });
};

/**
 * How long a refresh may go unanswered before it counts as failed. A refresh
 * normally answers in well under a second. Without a bound, a hung one holds
 * `auth-token`'s single in-flight refresh forever: every later refresh in the
 * tab joins or waits on it, and none is ever sent (DV-3745 review).
 */
export const REFRESH_REQUEST_TIMEOUT_MS = 30_000;

export const getAccessTokenFromRefreshTokenAPI = (
  refreshToken: string,
): Promise<AxiosResponse<TokenResponse>> => {
  const instance = axios.create({ timeout: REFRESH_REQUEST_TIMEOUT_MS });
  delete instance.defaults.headers.common.Authorization;
  return instance.post(getAPIUrls('/api/v1/refreshToken'), {
    refreshToken,
  });
};

export const endSessionApi = (): Promise<AxiosResponse<unknown>> => {
  const instance = axios.create();
  delete instance.defaults.headers.common.Authorization;
  return instance.post(getAPIUrls('/api/v1/logout'), {
    refreshToken: getParsedAccessToken()?.refresh_token ?? null,
  });
};

/**
 * @param isLogin - true for the authorization-code exchange, false for a
 * silent refresh.
 *
 * <p>DV-3573 review finding 1. `currentContext` and `role` are derived from the
 * token's `CurrentContext` claim, and that was correct while an account switch
 * refreshed the token: the claim moved with the account. It no longer does, so
 * re-deriving them on a REFRESH would quietly reset both to whatever account
 * the session logged in to — minutes after a switch, with nothing on screen to
 * say so. That is how a team invite ended up in the wrong account
 * (`teamApi.ts` falls back to `getCurrentContext()`), and how a route guard
 * ended up reading another account's role.
 *
 * <p>Login still writes them: that is the one moment the claim IS the truth.
 *
 * <p>Required rather than defaulted, deliberately. A default of `true` is the
 * login behaviour, so a refresh path added later that simply forgot the
 * argument would compile, pass review as a one-line call, and silently
 * reinstate the defect above. There is no safe default here — the two
 * callers mean opposite things — so the type system asks.
 */
export const processTokenData = (token: TokenResponse, isLogin: boolean): JwtPayload => {
  // Parse before persisting anything: a malformed token should throw and
  // leave localStorage untouched, not half-populate the new token with
  // stale claims.
  const parsedTokenData = parseJwt(token.access_token);

  // DV-3421: never let a refresh replace a NEWER token for the same user.
  // Refreshes are not coordinated across tabs, so one begun before another
  // tab's cross-tree switch wrote the user's attributes can answer after that
  // switch's own refresh — and would write a token minted from the OLD
  // attributes over the new one, re-anchoring every tab to the old tree. This
  // is the one place the token is written, so refusing here means no reader
  // has to second-guess what is stored. Login always writes: it is a new
  // session by definition.
  if (!isLogin) {
    const newer = newerStoredTokenFor(parsedTokenData);
    if (newer) return newer;
  }

  localStorage.setItem(LoginConstants.ACCESS_TOKEN, JSON.stringify(token));
  localStorage.setItem('isLogin', JSON.stringify(true));

  localStorage.setItem(
    'subEntities',
    parsedTokenData.SubEntities !== undefined
      ? parsedTokenData.SubEntities
      : JSON.stringify([]),
  );

  sessionStorage.removeItem(LoginConstants.CODE_VERIFIER);
  sessionStorage.removeItem(LoginConstants.IS_REGISTER);
  sessionStorage.removeItem(LoginConstants.IDP_HINT);
  // Written only when the claim exists. It used to be interpolated
  // unconditionally, so an absent claim persisted as the literal "undefined"
  // and every downstream reader took that for an entity name (DV-3012 / D12).
  if (isLogin) {
    if (parsedTokenData.CurrentContext) {
      localStorage.setItem('currentContext', parsedTokenData.CurrentContext);
    } else {
      localStorage.removeItem('currentContext');
    }
  }
  localStorage.setItem(
    'userName',
    `${parsedTokenData.given_name} ${parsedTokenData.family_name}`,
  );

  // One table with switchEntity's (DV-2751 was two drifting copies): a
  // post-processing user whose role was never written resolved every route to
  // 'no-role' and was denied.
  const realmRole = realmRoleInContext(parsedTokenData.CurrentContext);
  if (isLogin && realmRole) {
    localStorage.setItem('role', realmRole);
  }
  // Role just changed hands — re-evaluate whether this session may use dark.
  applyTheme();

  // Optional-chained: a malformed realm_access claim shouldn't crash login.
  const isContained =
    parsedTokenData.realm_access?.roles?.includes('default-roles-planomatic') ??
    false;

  localStorage.setItem('defaultRole', JSON.stringify(isContained));

  return parsedTokenData;
};

/**
 * The stored token's claims when they belong to the same user as `incoming`
 * and were issued strictly LATER, else null. Equal issue times are not refused:
 * `iat` has one-second resolution, and refusing ties would also refuse a
 * legitimate refresh landing in the same second as the token it replaces.
 */
const newerStoredTokenFor = (incoming: JwtPayload): JwtPayload | null => {
  try {
    const raw = localStorage.getItem(LoginConstants.ACCESS_TOKEN);
    if (!raw) return null;
    const stored = parseJwt((JSON.parse(raw) as TokenResponse).access_token);
    return stored.sub === incoming.sub &&
      typeof stored.iat === 'number' &&
      typeof incoming.iat === 'number' &&
      stored.iat > incoming.iat
      ? stored
      : null;
  } catch {
    return null;
  }
};

export const getAccessToken = (): string | null => {
  return localStorage.getItem(LoginConstants.ACCESS_TOKEN);
};
export const getParsedAccessToken = (): TokenResponse | null => {
  try {
    const raw = getAccessToken();
    if (!raw) return null;
    return JSON.parse(raw) as TokenResponse;
  } catch {
    // Corrupted token — remove it so the app can recover on next load
    localStorage.removeItem(LoginConstants.ACCESS_TOKEN);
    return null;
  }
};

export const isRegistration = (): boolean => {
  return !!sessionStorage.getItem(LoginConstants.IS_REGISTER);
};

export const getIdpHint = (): string | null => {
  return sessionStorage.getItem(LoginConstants.IDP_HINT);
};
